/**
 * Pure helpers behind the dungeon store: what may be queued, how a server answer folds into the
 * client's view, and which sounds a turn's events deserve. No DOM, no network.
 */
import type { ActResponse, DungeonEvent, DungeonIntent, DungeonView, RunEnd } from '@xox/shared'
import type { Effect } from '../lib/feedback'

/** Taps ahead the client holds while a batch is in flight. */
export const MAX_QUEUED = 3
/** Intents sent per request at most (the server accepts up to 8). */
export const BATCH_SIZE = 6
export const RETRY_DELAYS_MS = [300, 800, 1500] as const
/** Two stale answers this close together mean another tab is playing the same run. */
export const STALE_WINDOW_MS = 10_000

export interface InFlight {
  batchId: string
  fromTurn: number
  intents: DungeonIntent[]
  attempt: number
}

export interface QueueView {
  view: DungeonView | null
  queue: DungeonIntent[]
  inFlight: InFlight | null
  end: RunEnd | null
}

/** Whether an intent may be queued right now (perks only while a pick is pending, and so on). */
export function canEnqueue(s: QueueView, intent: DungeonIntent): boolean {
  const v = s.view
  if (v?.status !== 'active') return false
  if (v.pendingPerks.length > 0)
    return intent.type === 'perk' && s.queue.length === 0 && !s.inFlight
  if (intent.type === 'perk') return false
  return s.queue.length < MAX_QUEUED
}

/** Folds a 200 answer in: the view is the truth; a short batch drops what was planned after it. */
export function reduceAct(
  s: QueueView,
  res: ActResponse,
  sent: number,
): Partial<QueueView> & { fx: DungeonEvent[] } {
  const shortened = res.applied < sent
  return {
    view: res.view,
    queue: shortened || res.view.status !== 'active' ? [] : s.queue,
    inFlight: null,
    end: res.end ?? s.end,
    fx: res.events,
  }
}

/** A 409: adopt the server's view, forget everything we meant to do from the old one. */
export function reduceStale(view: DungeonView): Partial<QueueView> {
  return { view, queue: [], inFlight: null }
}

/** Sounds and buzzes for a turn's events, loudest first. */
export function deriveDungeonEffects(events: readonly DungeonEvent[]): Effect[] {
  const out = new Set<Effect>()
  for (const e of events) {
    switch (e.type) {
      case 'won':
        out.add('win')
        break
      case 'died':
        out.add('lose')
        break
      case 'levelUp':
        out.add('levelUp')
        break
      case 'secondWind':
        out.add('cheer')
        break
      case 'descended':
        out.add('descend')
        break
      case 'heroAttacked':
      case 'monsterHurt':
        out.add('hit')
        break
      case 'heroHurt':
        out.add('hurt')
        break
      case 'gold':
        out.add('coin')
        break
      case 'pickedUp':
      case 'chestOpened':
        out.add('key')
        break
      case 'frozen':
        out.add('frost')
        break
      case 'used':
      case 'teleported':
        out.add('spell')
        break
      case 'inventoryFull':
        out.add('thud')
        break
      default:
        break
    }
  }
  // one hit sound is plenty when the hero also got hurt
  if (out.has('hurt')) out.delete('hit')
  return [...out]
}
